import React, { useRef, useState, useEffect, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";

const FONT_LINK_ID = "esig-fonts";
const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Great+Vibes&family=Caveat:wght@600&family=Pacifico&family=Pinyon+Script&display=swap";

function ensureFontsLoaded() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

const SIGNATURE_FONTS = [
  { label: "Dancing Script", value: "'Dancing Script', cursive" },
  { label: "Great Vibes", value: "'Great Vibes', cursive" },
  { label: "Caveat", value: "'Caveat', cursive" },
  { label: "Pacifico", value: "'Pacifico', cursive" },
  { label: "Pinyon Script", value: "'Pinyon Script', cursive" },
];

function typedTextToFile(text: string, font: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    if (!ctx) return reject(new Error("Canvas not supported"));
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = `52px ${font}`;
    ctx.fillStyle = "#1a1a2e";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("toBlob failed"));
      resolve(new File([blob], "signature.png", { type: "image/png" }));
    }, "image/png");
  });
}

function dataUrlToFile(dataUrl: string): File {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], "signature.png", { type: mime });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ESignaturePadProps<TField extends string = string> {
  field: TField; // ← key into formData
  onChange: (field: TField, file: File | null) => void;
  existingSignatureUrl?: string | null;
  error?: string;
  isDisabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

function ESignaturePad<TField extends string = string>({
  field,
  onChange,
  existingSignatureUrl,
  error,
  isDisabled = false,
}: ESignaturePadProps<TField>) {
  type Mode = "draw" | "type";
  const [mode, setMode] = useState<Mode>("draw");
  const [typedName, setTypedName] = useState("");
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0].value);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  const clearSignature = useCallback(() => {
    sigCanvasRef.current?.clear();
    setTypedName("");
    setPreview(null);
    setConfirmed(false);
    onChange(field, null);
  }, [field, onChange]);

  const switchMode = (next: Mode) => {
    clearSignature();
    setMode(next);
  };

  const handleDrawEnd = () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;
    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    onChange(field, dataUrlToFile(dataUrl));
    setPreview(dataUrl);
    setConfirmed(true);
  };

  const handleApplyTyped = async () => {
    if (!typedName.trim()) return;
    const file = await typedTextToFile(typedName.trim(), selectedFont);
    const url = URL.createObjectURL(file);
    setPreview(url);
    setConfirmed(true);
    onChange(field, file);
  };

  useEffect(() => {
    if (isDisabled) {
      sigCanvasRef.current?.off();
    } else {
      sigCanvasRef.current?.on();
    }
  }, [isDisabled]);

  return (
    <div style={{ maxWidth: 520 }}>
      {/* Tab switcher */}
      <div
        style={{
          display: "flex",
          border: "1px solid #dee2e6",
          borderRadius: 8,
          overflow: "hidden",
          marginBottom: 12,
          width: "fit-content",
        }}
      >
        {(["draw", "type"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            style={{
              padding: "5px 18px",
              fontSize: "0.82rem",
              fontWeight: 500,
              border: "none",
              borderRight: m === "draw" ? "1px solid #dee2e6" : "none",
              cursor: isDisabled ? "not-allowed" : "pointer",
              background: mode === m ? "#fd7e14" : "#fff",
              color: mode === m ? "#fff" : "#495057",
            }}
            disabled={isDisabled}
          >
            {m === "draw" ? "✏️ Draw" : "🔤 Type"}
          </button>
        ))}
      </div>

      {/* Draw pad */}
      {mode === "draw" && !confirmed && (
        <div>
          <div
            style={{
              border: "1.5px dashed #adb5bd",
              borderRadius: 8,
              background: "#fff",
              overflow: "hidden",
              width: 480,
              height: 150,
            }}
          >
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="#1a1a2e"
              canvasProps={{ width: 480, height: 150 }}
              onEnd={handleDrawEnd}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 5,
            }}
          >
            <small style={{ color: "#6c757d", fontSize: "0.75rem" }}>
              Sign using mouse or touch
            </small>
            {/* <button
              type="button"
              onClick={clearSignature}
              style={{
                background: "none",
                border: "none",
                color: "#dc3545",
                fontSize: "0.8rem",
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
              disabled={isDisabled}
            >
              Clear
            </button> */}
          </div>
        </div>
      )}

      {/* Type pad */}
      {mode === "type" && !confirmed && (
        <div>
          <input
            type="text"
            className="form-control form-control-sm mb-2 text-capitaliz"
            placeholder="Type full name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            style={{ maxWidth: 480 }}
            readOnly={isDisabled}
          />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 10,
            }}
          >
            {SIGNATURE_FONTS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setSelectedFont(f.value)}
                style={{
                  padding: "3px 12px",
                  borderRadius: 20,
                  cursor: "pointer",
                  fontFamily: f.value,
                  fontSize: "1rem",
                  border: `1.5px solid ${selectedFont === f.value ? "#fd7e14" : "#dee2e6"}`,
                  background: selectedFont === f.value ? "#fff3e8" : "#fff",
                  color: selectedFont === f.value ? "#fd7e14" : "#495057",
                }}
                disabled={isDisabled}
              >
                {f.label}
              </button>
            ))}
          </div>

          {typedName && (
            <div
              style={{
                border: "1.5px dashed #adb5bd",
                borderRadius: 8,
                background: "#fff",
                width: 480,
                height: 90,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontFamily: selectedFont,
                  fontSize: "2.4rem",
                  color: "#1a1a2e",
                }}
              >
                {typedName}
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={handleApplyTyped}
            disabled={!typedName.trim()}
            style={{
              background: "#fd7e14",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "4px 16px",
              fontSize: "0.82rem",
              opacity: typedName.trim() ? 1 : 0.5,
              cursor: typedName.trim() ? "pointer" : "not-allowed",
            }}
          >
            Apply Signature
          </button>
        </div>
      )}

      {/* Confirmed preview */}
      {confirmed && preview && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            border: "1.5px solid #adb5bd",
            borderRadius: 8,
            background: "#fff",
            padding: "6px 12px",
          }}
        >
          <img
            src={preview}
            alt="signature"
            style={{ maxHeight: 56, maxWidth: 240, objectFit: "contain" }}
          />
          <button
            type="button"
            onClick={clearSignature}
            style={{
              background: "none",
              border: "1px solid #dc3545",
              color: "#dc3545",
              borderRadius: 6,
              padding: "2px 10px",
              fontSize: "0.78rem",
              cursor: "pointer",
            }}
          >
            ✕ Redo
          </button>
        </div>
      )}

      {/* Existing saved signature (edit mode) */}
      {!confirmed && !preview && existingSignatureUrl && (
        <div style={{ marginTop: 6 }}>
          <small style={{ color: "#6c757d", fontSize: "0.75rem" }}>
            Saved signature:
          </small>
          <div
            style={{
              marginTop: 4,
              marginLeft: 7,
              border: "1px solid #dee2e6",
              borderRadius: 6,
              background: "#fff",
              padding: "5px 10px",
              display: "inline-block",
            }}
          >
            <img
              src={existingSignatureUrl}
              alt="saved"
              style={{ maxHeight: 56, maxWidth: 220, objectFit: "contain" }}
            />
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: "#dc3545", fontSize: "0.78rem", marginTop: 5 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default ESignaturePad;
